import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ApiActions } from '../shared/apiActions';

export default function ImageSearchTest() {
  const [searchTerm, setSearchTerm] = useState('');
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchCount, setSearchCount] = useState(0);

  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Note: Authentication is handled by ProtectedRoute wrapper, so currentUser is guaranteed to exist

  // Function to clear all states and reset the form
  const handleClear = () => {
    setSearchTerm('');
    setImages([]);
    setError('');
  };

  const handleSearch = async (e) => {
    e?.preventDefault(); // Handle both button click and form submit

    if (!searchTerm.trim()) {
      setError('Please enter a search term');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      // Get the auth token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`/api/model?action=${ApiActions.GET_IMAGES_BY_TERM}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // Add the auth token
        },
        body: JSON.stringify({
          term: searchTerm,
          limit: 3,
          provider: 'google_custom'
        }),
      });

      if (response.status === 401) {
        // Token expired or invalid
        navigate('/');
        throw new Error('Session expired. Please login again.');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch images');
      }

      setImages(data.images || []);
      setSearchCount(prev => prev + 1);
    } catch (err) {
      setError(err.message);
      setImages([]);
    } finally {
      setIsLoading(false);
    }
  };

  // If not logged in, don't render anything (useEffect will redirect)
  if (!currentUser) {
    return null;
  }

  // Render search statistics
  const renderStats = () => {
    if (searchCount === 0) return null;

    return (
      <Alert variant="info" className="mt-3">
        Searches performed: {searchCount}
      </Alert>
    );
  };

  // Render search results
  const renderResults = () => {
    if (images.length === 0) return null;

    return (
      <Row className="mt-4">
        {images.map((image, index) => (
          <Col key={index} xs={12} sm={6} md={4} className="mb-4">
            <Card>
              <Card.Img
                variant="top"
                src={image.url}
                alt={`Result ${index + 1}`}
                style={{ height: '200px', objectFit: 'cover' }}
              />
              <Card.Body>
                <Card.Text className="small">
                  Source:{' '}
                  <a href={image.sourceUrl} target="_blank" rel="noopener noreferrer">
                    {image.attribution.name}
                  </a>
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={8}>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">Image Search Test</h4>
            </Card.Header>
            <Card.Body>
              {/* User info */}
              <Alert variant="info" className="mb-4">
                Logged in as: {currentUser?.email}
              </Alert>

              {/* Search form */}
              <Form onSubmit={handleSearch}>
                <Row className="align-items-end">
                  <Col xs={12} md={8}>
                    <Form.Group>
                      <Form.Label>Search Term</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter a location or attraction name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        disabled={isLoading}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={4} className="mt-3 mt-md-0">
                    <div className="d-flex gap-2">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isLoading}
                        className="flex-grow-1"
                      >
                        {isLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Searching...
                          </>
                        ) : (
                          'Search'
                        )}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleClear}
                        disabled={isLoading}
                      >
                        Clear
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>

              {/* Error message */}
              {error && (
                <Alert variant="danger" className="mt-3">
                  {error}
                </Alert>
              )}

              {/* Stats */}
              {renderStats()}

              {/* Results */}
              {renderResults()}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
} 